import { useEffect, useState } from 'react';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import useLicenseTypeManagement from './hooks/useLicenseTypeManagement';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loading from '../../Common/components/Loading';
import * as Yup from 'yup';
import { toast } from 'sonner';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent } from '@/src/shadComponents/ui/card';
import { Loader2 } from 'lucide-react';
import PageShell from '@/src/Common/components/PageShell';

interface CreateLicenseFormValues {
    orgId: number;
    licenseTypeId: number;
    expiresAt: string | null;
}

interface Organization {
    id: number;
    name: string;
}

interface LicenseType {
    id: number;
    name: string;
    key: string;
    description: string | null;
    adminName: string;
    simultaneousDeviceCount: number;
}

const validationSchema = Yup.object({
    orgId: Yup.number().required('Organization is required'),
    licenseTypeId: Yup.number().required('License type is required'),
    expiresAt: Yup.string().nullable(),
});

const initialValues: CreateLicenseFormValues = {
    orgId: 0,
    licenseTypeId: 0,
    expiresAt: null,
};

export default function CreateLicense() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLoading, createLicense, fetchLicenseTypes, setIsLoading } =
        useLicenseTypeManagement();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
    const { listOrgs } = useOrgs();

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            try {
                // TODO: Replace with actual API call to fetch organizations
                const orgs = await listOrgs();
                if (isMounted) setOrganizations(orgs);

                const types = await fetchLicenseTypes();
                if (isMounted) setLicenseTypes(types);
            } catch (error) {
                console.error('Failed to load data:', error);
                if (error instanceof Error) {
                    toast.error(error.message || 'Failed to load data');
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    const orgId = searchParams.get('orgId');
    if (orgId) {
        initialValues.orgId = parseInt(orgId);
    }

    return (
        <PageShell
            title="Create License"
            breadcrumbs={[
                { label: 'Admin', href: '/admin/licenses' },
                { label: 'Create License' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        await createLicense(values);
                        toast.success('License created successfully');
                        navigate(-1);
                    } catch (error) {
                        console.error('Failed to create license:', error);
                        if (error instanceof Error) {
                            toast.error(
                                error.message || 'Failed to create license',
                            );
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <FormikForm
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        License Details
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Select the organization and license
                                        type.
                                    </p>
                                </div>

                                <Card className="md:col-span-2">
                                    <CardContent className="pt-6 space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="orgId">Organization</Label>
                                            <Field
                                                as="select"
                                                id="orgId"
                                                name="orgId"
                                                className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:max-w-md"
                                            >
                                                <option value="">
                                                    Select an organization
                                                </option>
                                                {organizations.map(
                                                    (org) => (
                                                        <option
                                                            key={org.id}
                                                            value={org.id}
                                                        >
                                                            {org.name}
                                                        </option>
                                                    ),
                                                )}
                                            </Field>
                                            <ErrorMessage
                                                name="orgId"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="licenseTypeId">License Type</Label>
                                            <Field
                                                as="select"
                                                id="licenseTypeId"
                                                name="licenseTypeId"
                                                className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 sm:max-w-md"
                                            >
                                                <option value="">
                                                    Select a license type
                                                </option>
                                                {licenseTypes.map(
                                                    (type) => (
                                                        <option
                                                            key={type.id}
                                                            value={type.id}
                                                        >
                                                            {type.name}
                                                        </option>
                                                    ),
                                                )}
                                            </Field>
                                            <ErrorMessage
                                                name="licenseTypeId"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="expiresAt">Expiration Date</Label>
                                            <Field
                                                as={Input}
                                                type="date"
                                                id="expiresAt"
                                                name="expiresAt"
                                                className="sm:max-w-md"
                                            />
                                            <ErrorMessage
                                                name="expiresAt"
                                                component="div"
                                                className="text-sm text-destructive"
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-4 pt-6">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                                {isSubmitting ? 'Creating...' : 'Create'}
                            </Button>
                        </div>
                    </FormikForm>
                )}
            </Formik>
        </PageShell>
    );
}
